'use client';

import React, {
  ButtonHTMLAttributes,
  FC,
  PropsWithChildren,
  useMemo,
  useRef,
} from 'react';
import { useTheme } from 'styled-components';

import useEventListener from '@/hooks/eventListener';
import { useServer } from '@/modules/server/context/ServerEnvContext';

import { ScButton, ScButtonProps, ScButtonWrapper, ScLink } from './styled';
import { Spinner } from '@/styles/common';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  target?: string;
  href?: string;
  to?: string;
  linkScroll?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  navigation?: boolean;
  onLinkClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
}

interface IHandleLinkWrappingProps {
  Component: React.ComponentType<any>;
  props: PropsWithChildren<ButtonProps>;
}

const HandleLinkWrapping = React.memo(
  ({ Component, props }: IHandleLinkWrappingProps) => {
    const {
      href,
      to,
      linkScroll = false,
      target,
      children,
      disabled,
      onLinkClick,
      navigation,
      type = 'button',
      ...rest
    } = props;
    const { blueArrowIcon: BlueArrowIcon } = useTheme();

    const button = (
      <Component disabled={disabled || rest.isLoading} type={type} {...rest}>
        <span className="text">{children}</span>
      </Component>
    );

    if (href) {
      return (
        <a
          className="button-link"
          href={href}
          target={target || '_blank'}
          rel={!target ? 'noopener noreferrer' : undefined}
        >
          {button}
        </a>
      );
    }

    if (navigation) {
      return (
        <ScButtonWrapper>
          {button}
          <BlueArrowIcon aria-hidden={true} />
        </ScButtonWrapper>
      );
    }

    if (to)
      return (
        <ScLink href={to} onClick={onLinkClick} scroll={linkScroll}>
          {button}
        </ScLink>
      );

    return button;
  }
);

const ButtonComp: FC<
  Omit<ScButtonProps, 'isMobileDevice'> & {
    children: React.ReactNode;
    isLoading?: boolean;
  }
> = ({ children, isLoading = false, ...props }) => {
  const {
    primary,
    primaryTransparent,
    secondary,
    secondaryTransparent,
    blackTransparent,
    white,
  } = props;

  const { mainColor, whiteColor, secondaryColor, blackColor } = useTheme();
  const { isMobileDevice } = useServer();

  const ref = useRef<HTMLButtonElement>(null);

  useEventListener(
    'mouseenter',
    (e) => {
      if (!ref.current) return;
      const relX = e.pageX - ref.current.getBoundingClientRect().left;
      const relY = e.clientY - ref.current.getBoundingClientRect().top;

      const element = ref.current.querySelector('div.aware') as HTMLDivElement;

      if (!element) return;
      element.style.top = `${relY}px`;
      element.style.left = `${relX}px`;
    },
    ref
  );

  const styleSpinner = useMemo(() => {
    switch (true) {
      case primary != null:
        return { color: whiteColor, background: mainColor };
      case secondary != null:
        return { color: whiteColor, background: secondaryColor };
      case secondaryTransparent != null:
        return { color: secondaryColor, background: blackColor };
      case blackTransparent != null:
        return { color: blackColor, background: whiteColor };
      case white != null:
        return { color: blackColor, background: whiteColor };
      default:
        return { color: whiteColor, background: blackColor };
    }
  }, [
    primary,
    secondary,
    secondaryTransparent,
    blackTransparent,
    white,
    mainColor,
    whiteColor,
    secondaryColor,
    blackColor,
  ]);
  return (
    <ScButton ref={ref} isMobileDevice={isMobileDevice} {...props}>
      {primaryTransparent && <span className="bg" />}
      {isLoading ? <Spinner size={20} {...styleSpinner} center /> : children}
      <span className="awareWrap">
        <div className="aware" />
      </span>
    </ScButton>
  );
};

export const Button: FC<
  ButtonProps &
    Omit<ScButtonProps, 'isMobileDevice'> & {
      component?: React.ComponentType<ButtonProps>;
    } & ButtonHTMLAttributes<HTMLButtonElement>
> = ({ component, ...rest }) => (
  <HandleLinkWrapping Component={component || ButtonComp} props={rest} />
);
